import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Loader2, ArrowLeft, Edit, Plus, Trash2, Download, Upload, FileText, GraduationCap, BookOpen, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  ativo: { label: 'Ativo', color: 'bg-green-100 text-green-800' },
  inativo: { label: 'Inativo', color: 'bg-gray-100 text-gray-800' },
  afastado: { label: 'Afastado', color: 'bg-yellow-100 text-yellow-800' },
  demitido: { label: 'Demitido', color: 'bg-red-100 text-red-800' },
};

const CURSO_STATUS_CONFIG = {
  em_andamento: { label: 'Em Andamento', color: 'bg-blue-100 text-blue-800' },
  concluido: { label: 'Concluído', color: 'bg-green-100 text-green-800' },
};

const emptyCursoForm = {
  nome: '',
  instituicao: '',
  data_inicio: '',
  data_conclusao: '',
  carga_horaria: '',
  status: 'em_andamento',
  observacoes: '',
};

const emptyCertForm = {
  nome_titulo: '',
  curso_id: '',
  instituicao_emissora: '',
  data_emissao: '',
  validade: '',
};

const emptyGradeForm = {
  nome: '',
  data_inicio: '',
  data_conclusao: '',
};

const EstagiarioDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Perfil
  const [perfilOpen, setPerfilOpen] = useState(false);
  const [perfilForm, setPerfilForm] = useState({});
  const [savingPerfil, setSavingPerfil] = useState(false);

  // Cursos
  const [cursoOpen, setCursoOpen] = useState(false);
  const [cursoForm, setCursoForm] = useState(emptyCursoForm);
  const [cursoEditando, setCursoEditando] = useState(null);
  const [savingCurso, setSavingCurso] = useState(false);

  // Certificados
  const [certOpen, setCertOpen] = useState(false);
  const [certForm, setCertForm] = useState(emptyCertForm);
  const [certEditando, setCertEditando] = useState(null);
  const [certFile, setCertFile] = useState(null);
  const [savingCert, setSavingCert] = useState(false);
  const certFileRef = useRef(null);

  // Grade Curricular
  const [gradeOpen, setGradeOpen] = useState(false);
  const [gradeForm, setGradeForm] = useState(emptyGradeForm);
  const [gradeEditando, setGradeEditando] = useState(null);
  const [savingGrade, setSavingGrade] = useState(false);

  // Importação por IA (OCR)
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importItems, setImportItems] = useState([]);
  const [importSaving, setImportSaving] = useState(false);
  const importFileRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/estagiarios/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar estagiário');
      navigate('/rh/estagiarios');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  // ---------- Perfil ----------
  const openPerfil = () => {
    const p = data?.perfil || {};
    setPerfilForm({
      instituicao_ensino: p.instituicao_ensino || '',
      cnpj_instituicao: p.cnpj_instituicao || '',
      curso: p.curso || '',
      turno: p.turno || '',
      orientador: p.orientador || '',
      estagio_inicio: p.estagio_inicio || '',
      estagio_fim: p.estagio_fim || '',
      observacoes: p.observacoes || '',
    });
    setPerfilOpen(true);
  };

  const savePerfil = async () => {
    try {
      setSavingPerfil(true);
      const token = localStorage.getItem('token');
      const payload = {};
      Object.keys(perfilForm).forEach((k) => {
        if (perfilForm[k]) payload[k] = perfilForm[k];
      });
      await axios.put(`${API}/api/estagiarios/${id}/perfil`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Perfil do estágio atualizado');
      setPerfilOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar perfil');
    } finally {
      setSavingPerfil(false);
    }
  };

  // ---------- Cursos ----------
  const openCursoNovo = () => {
    setCursoEditando(null);
    setCursoForm(emptyCursoForm);
    setCursoOpen(true);
  };

  const openCursoEditar = (curso) => {
    setCursoEditando(curso);
    setCursoForm({
      nome: curso.nome || '',
      instituicao: curso.instituicao || '',
      data_inicio: curso.data_inicio || '',
      data_conclusao: curso.data_conclusao || '',
      carga_horaria: curso.carga_horaria != null ? String(curso.carga_horaria) : '',
      status: curso.status || 'em_andamento',
      observacoes: curso.observacoes || '',
    });
    setCursoOpen(true);
  };

  const saveCurso = async () => {
    try {
      setSavingCurso(true);
      const token = localStorage.getItem('token');
      const payload = { ...cursoForm };
      if (!payload.nome.trim()) {
        toast.error('Informe o nome do curso');
        return;
      }
      if (payload.carga_horaria) payload.carga_horaria = Number(payload.carga_horaria);
      const url = cursoEditando
        ? `${API}/api/estagiarios/${id}/cursos/${cursoEditando.id}`
        : `${API}/api/estagiarios/${id}/cursos`;
      const method = cursoEditando ? axios.put : axios.post;
      await method(url, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(cursoEditando ? 'Curso atualizado' : 'Curso cadastrado');
      setCursoOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar curso');
    } finally {
      setSavingCurso(false);
    }
  };

  const excluirCurso = async (curso) => {
    if (!window.confirm('Excluir este curso?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/estagiarios/${id}/cursos/${curso.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Curso excluído');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir curso');
    }
  };

  // ---------- Certificados ----------
  const openCertNovo = () => {
    setCertEditando(null);
    setCertForm(emptyCertForm);
    setCertFile(null);
    if (certFileRef.current) certFileRef.current.value = '';
    setCertOpen(true);
  };

  const openCertEditar = (cert) => {
    setCertEditando(cert);
    setCertForm({
      nome_titulo: cert.nome_titulo || '',
      curso_id: cert.curso_id || '',
      instituicao_emissora: cert.instituicao_emissora || '',
      data_emissao: cert.data_emissao || '',
      validade: cert.validade || '',
    });
    setCertFile(null);
    if (certFileRef.current) certFileRef.current.value = '';
    setCertOpen(true);
  };

  const saveCert = async () => {
    try {
      setSavingCert(true);
      const token = localStorage.getItem('token');
      if (!certForm.nome_titulo.trim()) {
        toast.error('Informe o nome/título do certificado');
        return;
      }
      const formData = new FormData();
      Object.keys(certForm).forEach((k) => {
        if (certForm[k]) formData.append(k, certForm[k]);
      });
      if (certFile && !certEditando) {
        formData.append('arquivo', certFile);
      }
      const headers = { Authorization: `Bearer ${token}` };
      if (certEditando) {
        await axios.put(`${API}/api/estagiarios/${id}/certificados/${certEditando.id}`, formData, { headers });
        if (certFile) {
          const uploadData = new FormData();
          uploadData.append('arquivo', certFile);
          await axios.post(`${API}/api/estagiarios/${id}/certificados/${certEditando.id}/arquivo`, uploadData, { headers });
        }
      } else {
        await axios.post(`${API}/api/estagiarios/${id}/certificados`, formData, { headers });
      }
      toast.success(certEditando ? 'Certificado atualizado' : 'Certificado cadastrado');
      setCertOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar certificado');
    } finally {
      setSavingCert(false);
    }
  };

  const excluirCert = async (cert) => {
    if (!window.confirm('Excluir este certificado?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/estagiarios/${id}/certificados/${cert.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Certificado excluído');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir certificado');
    }
  };

  const downloadCert = async (cert) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/estagiarios/${id}/certificados/${cert.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = cert.arquivo_nome || 'certificado';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar certificado');
    }
  };

  // ---------- Grade Curricular ----------
  const openGradeNovo = () => {
    setGradeEditando(null);
    setGradeForm(emptyGradeForm);
    setGradeOpen(true);
  };

  const openGradeEditar = (item) => {
    setGradeEditando(item);
    setGradeForm({
      nome: item.nome || '',
      data_inicio: item.data_inicio || '',
      data_conclusao: item.data_conclusao || '',
    });
    setGradeOpen(true);
  };

  const saveGrade = async () => {
    try {
      setSavingGrade(true);
      const token = localStorage.getItem('token');
      if (!gradeForm.nome.trim()) {
        toast.error('Informe o nome do item da grade curricular');
        return;
      }
      const payload = { ...gradeForm };
      const url = gradeEditando
        ? `${API}/api/estagiarios/${id}/grade/${gradeEditando.id}`
        : `${API}/api/estagiarios/${id}/grade`;
      const method = gradeEditando ? axios.put : axios.post;
      await method(url, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(gradeEditando ? 'Item da grade atualizado' : 'Item da grade cadastrado');
      setGradeOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar item da grade');
    } finally {
      setSavingGrade(false);
    }
  };

  const excluirGrade = async (item) => {
    if (!window.confirm('Excluir este item da grade curricular?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/estagiarios/${id}/grade/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Item da grade excluído');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir item da grade');
    }
  };

  // ---------- Importação por IA (OCR) ----------
  const openImport = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportItems([]);
    if (importFileRef.current) importFileRef.current.value = '';
    setImportOpen(true);
  };

  const lerImport = async () => {
    if (!importFile) {
      toast.error('Selecione um arquivo PDF, Excel ou CSV');
      return;
    }
    if (importFile.size > 20 * 1024 * 1024) {
      toast.error('O arquivo deve ter no máximo 20 MB');
      return;
    }
    try {
      setImporting(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await axios.post(`${API}/api/estagiarios/${id}/grade/import`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setImportPreview(response.data);
      setImportItems((response.data.items || []).map((item) => ({
        nome: item.nome || '',
        data_inicio: item.data_inicio || '',
        data_conclusao: item.data_conclusao || '',
      })));
      if (!response.data.ok) {
        toast.warning(response.data.ai_error || 'A IA não conseguiu extrair os itens');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível ler o arquivo');
    } finally {
      setImporting(false);
    }
  };

  const updateImportItem = (index, patch) => {
    setImportItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const removeImportItem = (index) => {
    setImportItems((prev) => prev.filter((_, i) => i !== index));
  };

  const confirmarImport = async () => {
    const validos = importItems.filter((item) => item.nome && item.nome.trim());
    if (validos.length === 0) {
      toast.error('Nenhum item válido para cadastrar');
      return;
    }
    try {
      setImportSaving(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/estagiarios/${id}/grade/bulk`, { items: validos }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${validos.length} item(ns) da grade cadastrado(s)`);
      setImportOpen(false);
      setImportPreview(null);
      setImportItems([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar itens da grade');
    } finally {
      setImportSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  if (!data) return null;

  const { employee, perfil, cursos, certificados, grade } = data;
  const statusConf = STATUS_CONFIG[(employee.situacao || '').toLowerCase()] || { label: employee.situacao || 'N/A', color: 'bg-gray-100 text-gray-800' };
  const cursoNomeById = (cursoId) => cursos.find((c) => c.id === cursoId)?.nome;

  const infoBasica = [
    ['Empresa', employee.empresa],
    ['Filial', employee.filiais],
    ['Cargo', employee.cargo],
    ['Departamento', employee.departamento],
    ['Matrícula', employee.matricula || '-'],
    ['Data de Admissão', formatDate(employee.admissao)],
    ['E-mail', employee.email || '-'],
    ['Telefone', employee.telefone || '-'],
    ['CPF', employee.cpf || '-'],
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/rh/estagiarios')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <GraduationCap className="h-6 w-6 text-blue-600" />
              {employee.nome}
            </h1>
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <Badge variant="outline">Estagiário</Badge>
              <Badge className={statusConf.color}>{statusConf.label}</Badge>
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate(`/funcionarios/detalhado/editar/${employee.id}`)}>
          <Edit className="h-4 w-4 mr-2" /> Editar Cadastro
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Informações Básicas do Funcionário</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {infoBasica.map(([label, value]) => (
              <div key={label}>
                <span className="text-sm text-gray-500">{label}</span>
                <p className="font-medium">{value}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Período do Estágio</CardTitle>
            <CardDescription>Instituição de ensino, curso e vigência do estágio</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={openPerfil}>
            <Edit className="h-4 w-4 mr-2" /> Editar
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="text-sm text-gray-500">Início</span>
              <p className="font-medium">{formatDate(perfil.estagio_inicio)}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Término</span>
              <p className="font-medium">{formatDate(perfil.estagio_fim)}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Instituição de Ensino</span>
              <p className="font-medium">{perfil.instituicao_ensino || '-'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">CNPJ da Instituição</span>
              <p className="font-medium">{perfil.cnpj_instituicao || '-'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Curso</span>
              <p className="font-medium">{perfil.curso || '-'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Turno</span>
              <p className="font-medium">{perfil.turno || '-'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Orientador</span>
              <p className="font-medium">{perfil.orientador || '-'}</p>
            </div>
            {perfil.observacoes && (
              <div className="md:col-span-3">
                <span className="text-sm text-gray-500">Observações</span>
                <p className="font-medium whitespace-pre-wrap">{perfil.observacoes}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Cursos</CardTitle>
            <CardDescription>Cursos realizados ou em andamento pelo estagiário</CardDescription>
          </div>
          <Button size="sm" onClick={openCursoNovo}>
            <Plus className="h-4 w-4 mr-2" /> Adicionar Curso
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="em_andamento">
            <TabsList>
              <TabsTrigger value="em_andamento">Em Andamento</TabsTrigger>
              <TabsTrigger value="concluido">Concluídos</TabsTrigger>
            </TabsList>
            {['em_andamento', 'concluido'].map((tabStatus) => (
              <TabsContent key={tabStatus} value={tabStatus} className="space-y-2 mt-4">
                {cursos.filter((c) => c.status === tabStatus).length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center">
                    {tabStatus === 'em_andamento' ? 'Nenhum curso em andamento' : 'Nenhum curso concluído'}
                  </p>
                ) : cursos.filter((c) => c.status === tabStatus).map((curso) => {
                  const cconf = CURSO_STATUS_CONFIG[curso.status] || { label: curso.status, color: 'bg-gray-100 text-gray-800' };
                  return (
                    <div key={curso.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{curso.nome}</p>
                        <p className="text-xs text-gray-500">
                          {curso.instituicao || 'Instituição não informada'}
                          {curso.carga_horaria != null ? ` · ${curso.carga_horaria}h` : ''}
                          {curso.data_inicio ? ` · Início: ${formatDate(curso.data_inicio)}` : ''}
                          {curso.data_conclusao ? ` · Conclusão: ${formatDate(curso.data_conclusao)}` : ''}
                        </p>
                        {curso.observacoes && <p className="text-xs text-gray-400 mt-1">{curso.observacoes}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={cconf.color}>{cconf.label}</Badge>
                        <Button variant="ghost" size="sm" onClick={() => openCursoEditar(curso)} title="Editar">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => excluirCurso(curso)} title="Excluir">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Certificados</CardTitle>
            <CardDescription>Certificados do estagiário e arquivos anexados</CardDescription>
          </div>
          <Button size="sm" onClick={openCertNovo}>
            <Plus className="h-4 w-4 mr-2" /> Cadastrar Certificado
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {certificados.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">Nenhum certificado cadastrado</p>
          ) : certificados.map((cert) => (
            <div key={cert.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="font-medium text-sm">{cert.nome_titulo}</p>
                  <p className="text-xs text-gray-500">
                    {cert.instituicao_emissora || 'Emissor não informado'}
                    {cert.data_emissao ? ` · Emissão: ${formatDate(cert.data_emissao)}` : ''}
                    {cert.validade ? ` · Válido até: ${formatDate(cert.validade)}` : ''}
                    {cert.curso_id ? ` · Curso: ${cursoNomeById(cert.curso_id) || '—'}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {cert.arquivo_nome ? (
                  <Button variant="ghost" size="sm" onClick={() => downloadCert(cert)} title="Baixar">
                    <Download className="h-4 w-4" />
                  </Button>
                ) : (
                  <Badge variant="outline" className="text-xs">Sem arquivo</Badge>
                )}
                <Button variant="ghost" size="sm" onClick={() => openCertEditar(cert)} title="Editar">
                  <Edit className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => excluirCert(cert)} title="Excluir">
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Grade Curricular</CardTitle>
            <CardDescription>Itens da grade curricular do estágio (disciplinas/módulos)</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={openImport}>
              <Sparkles className="h-4 w-4 mr-2 text-violet-500" /> Importar com IA
            </Button>
            <Button size="sm" onClick={openGradeNovo}>
              <Plus className="h-4 w-4 mr-2" /> Adicionar Item
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {grade.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">Nenhum item da grade cadastrado</p>
          ) : grade.map((item) => (
            <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <BookOpen className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="font-medium text-sm">{item.nome}</p>
                  <p className="text-xs text-gray-500">
                    {item.data_inicio ? `Início: ${formatDate(item.data_inicio)}` : 'Início não informado'}
                    {item.data_conclusao ? ` · Conclusão: ${formatDate(item.data_conclusao)}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => openGradeEditar(item)} title="Editar">
                  <Edit className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => excluirGrade(item)} title="Excluir">
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Modal Perfil */}
      <Dialog open={perfilOpen} onOpenChange={setPerfilOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Período do Estágio</DialogTitle>
            <DialogDescription>Informações do estágio do colaborador</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="estagio_inicio">Início do estágio</Label>
              <Input id="estagio_inicio" type="date" value={perfilForm.estagio_inicio || ''} onChange={(e) => setPerfilForm({ ...perfilForm, estagio_inicio: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="estagio_fim">Término do estágio</Label>
              <Input id="estagio_fim" type="date" value={perfilForm.estagio_fim || ''} onChange={(e) => setPerfilForm({ ...perfilForm, estagio_fim: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="instituicao_ensino">Instituição de ensino</Label>
              <Input id="instituicao_ensino" value={perfilForm.instituicao_ensino || ''} onChange={(e) => setPerfilForm({ ...perfilForm, instituicao_ensino: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cnpj_instituicao">CNPJ da instituição</Label>
              <Input id="cnpj_instituicao" value={perfilForm.cnpj_instituicao || ''} onChange={(e) => setPerfilForm({ ...perfilForm, cnpj_instituicao: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="curso">Curso</Label>
              <Input id="curso" value={perfilForm.curso || ''} onChange={(e) => setPerfilForm({ ...perfilForm, curso: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="turno">Turno</Label>
              <Input id="turno" value={perfilForm.turno || ''} onChange={(e) => setPerfilForm({ ...perfilForm, turno: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="orientador">Orientador</Label>
              <Input id="orientador" value={perfilForm.orientador || ''} onChange={(e) => setPerfilForm({ ...perfilForm, orientador: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea id="observacoes" rows={3} value={perfilForm.observacoes || ''} onChange={(e) => setPerfilForm({ ...perfilForm, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPerfilOpen(false)}>Cancelar</Button>
            <Button onClick={savePerfil} disabled={savingPerfil}>
              {savingPerfil ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Edit className="h-4 w-4 mr-2" />} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Curso */}
      <Dialog open={cursoOpen} onOpenChange={setCursoOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{cursoEditando ? 'Editar Curso' : 'Novo Curso'}</DialogTitle>
            <DialogDescription>Cadastro de curso do estagiário</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="curso_nome">Nome do curso *</Label>
              <Input id="curso_nome" value={cursoForm.nome} onChange={(e) => setCursoForm({ ...cursoForm, nome: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="curso_instituicao">Instituição/Provedor</Label>
              <Input id="curso_instituicao" value={cursoForm.instituicao} onChange={(e) => setCursoForm({ ...cursoForm, instituicao: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="curso_data_inicio">Data de início</Label>
              <Input id="curso_data_inicio" type="date" value={cursoForm.data_inicio} onChange={(e) => setCursoForm({ ...cursoForm, data_inicio: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="curso_data_conclusao">Data de conclusão</Label>
              <Input id="curso_data_conclusao" type="date" value={cursoForm.data_conclusao} onChange={(e) => setCursoForm({ ...cursoForm, data_conclusao: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="curso_carga_horaria">Carga horária (horas)</Label>
              <Input id="curso_carga_horaria" type="number" min="0" value={cursoForm.carga_horaria} onChange={(e) => setCursoForm({ ...cursoForm, carga_horaria: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={cursoForm.status} onValueChange={(v) => setCursoForm({ ...cursoForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="concluido">Concluído</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="curso_observacoes">Observações</Label>
              <Textarea id="curso_observacoes" rows={3} value={cursoForm.observacoes} onChange={(e) => setCursoForm({ ...cursoForm, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCursoOpen(false)}>Cancelar</Button>
            <Button onClick={saveCurso} disabled={savingCurso}>
              {savingCurso ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Certificado */}
      <Dialog open={certOpen} onOpenChange={setCertOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{certEditando ? 'Editar Certificado' : 'Novo Certificado'}</DialogTitle>
            <DialogDescription>Certificado do estagiário e arquivo anexado</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="cert_nome">Nome/Título do certificado *</Label>
              <Input id="cert_nome" value={certForm.nome_titulo} onChange={(e) => setCertForm({ ...certForm, nome_titulo: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Curso associado</Label>
              <Select value={certForm.curso_id || 'none'} onValueChange={(v) => setCertForm({ ...certForm, curso_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {cursos.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cert_emissora">Instituição emissora</Label>
              <Input id="cert_emissora" value={certForm.instituicao_emissora} onChange={(e) => setCertForm({ ...certForm, instituicao_emissora: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cert_data_emissao">Data de emissão</Label>
              <Input id="cert_data_emissao" type="date" value={certForm.data_emissao} onChange={(e) => setCertForm({ ...certForm, data_emissao: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cert_validade">Validade</Label>
              <Input id="cert_validade" type="date" value={certForm.validade} onChange={(e) => setCertForm({ ...certForm, validade: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="cert_file">Arquivo do certificado</Label>
              <Input
                id="cert_file"
                ref={certFileRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                onChange={(e) => setCertFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-gray-500">
                {certEditando?.arquivo_nome ? `Arquivo atual: ${certEditando.arquivo_nome}. ` : ''}
                Selecione um arquivo para anexar ou substituir.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCertOpen(false)}>Cancelar</Button>
            <Button onClick={saveCert} disabled={savingCert}>
              {savingCert ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Grade Curricular */}
      <Dialog open={gradeOpen} onOpenChange={setGradeOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{gradeEditando ? 'Editar Item da Grade' : 'Novo Item da Grade'}</DialogTitle>
            <DialogDescription>Item da grade curricular do estágio</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="grade_nome">Nome/Disciplina/Módulo *</Label>
              <Input id="grade_nome" value={gradeForm.nome} onChange={(e) => setGradeForm({ ...gradeForm, nome: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="grade_data_inicio">Data de início</Label>
              <Input id="grade_data_inicio" type="date" value={gradeForm.data_inicio} onChange={(e) => setGradeForm({ ...gradeForm, data_inicio: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="grade_data_conclusao">Data de conclusão</Label>
              <Input id="grade_data_conclusao" type="date" value={gradeForm.data_conclusao} onChange={(e) => setGradeForm({ ...gradeForm, data_conclusao: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGradeOpen(false)}>Cancelar</Button>
            <Button onClick={saveGrade} disabled={savingGrade}>
              {savingGrade ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Importação por IA (OCR) */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-500" /> Importar Grade Curricular com IA</DialogTitle>
            <DialogDescription>Envie um PDF, Excel (.xlsx/.xls) ou CSV. A IA lê o arquivo (com OCR se necessário) e propõe os itens da grade para revisão.</DialogDescription>
          </DialogHeader>

          {!importPreview ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="grade_import_file">Arquivo (PDF, Excel ou CSV)</Label>
                <Input
                  id="grade_import_file"
                  ref={importFileRef}
                  type="file"
                  accept=".pdf,.xlsx,.xls,.csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                />
                <p className="text-xs text-gray-500">Até 20 MB. PDFs escaneados passam por OCR.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing}>Cancelar</Button>
                <Button onClick={lerImport} disabled={!importFile || importing}>
                  {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  {importing ? 'Lendo e analisando...' : 'Ler e analisar com IA'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md bg-violet-50 p-3 text-sm">
                <p>
                  Arquivo: {importFile?.name} · {importFile?.name?.split('.').pop()?.toUpperCase() === 'PDF' ? 'PDF (OCR aplicado se escaneado)' : 'Texto nativo extraído'}
                </p>
                {importPreview.extraction?.used_ocr && <p className="text-xs text-violet-700 mt-1">OCR aplicado no documento.</p>}
                {importPreview?.ai_error && (
                  <p className="mt-1 rounded bg-amber-50 p-2 text-amber-800">{importPreview.ai_error}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>{importItems.length} item(ns) extraído(s) — revise antes de salvar</Label>
                </div>
                {importItems.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center">Nenhum item foi extraído.</p>
                ) : (
                  <div className="space-y-2">
                    {importItems.map((item, index) => (
                      <div key={index} className="flex items-end gap-2 rounded-lg border p-2">
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs">Nome/Disciplina *</Label>
                          <Input value={item.nome} onChange={(e) => updateImportItem(index, { nome: e.target.value })} />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Início</Label>
                          <Input type="date" value={item.data_inicio} onChange={(e) => updateImportItem(index, { data_inicio: e.target.value })} />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Conclusão</Label>
                          <Input type="date" value={item.data_conclusao} onChange={(e) => updateImportItem(index, { data_conclusao: e.target.value })} />
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeImportItem(index)} title="Remover">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <details className="mt-3 text-xs text-gray-500">
                  <summary className="cursor-pointer">Ver texto extraído</summary>
                  <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-3 text-xs">{importPreview?.extraction?.text_preview || '—'}</pre>
                </details>
              </div>

              <DialogFooter className="flex items-center justify-between gap-2">
                <Button variant="outline" onClick={() => { setImportPreview(null); setImportItems([]); }} disabled={importSaving}>Voltar</Button>
                <Button onClick={confirmarImport} disabled={importSaving || importItems.length === 0}>
                  {importSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  {importSaving ? 'Salvando...' : `Confirmar e cadastrar ${importItems.length} itens`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EstagiarioDetailPage;
